// src/pages/CartDetailPage.tsx
import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Alert, Divider, Chip
} from '@mui/material';
import { useCartStore } from '../../store/cartStore';
import { formatCurrency } from '../../utils/currency';
function formatDate(dateStr?: string | null) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  }).format(d);
}
const statusToColor = (s?: string | null) =>
  s === 'placed' ? 'success' : s === 'cancelled' ? 'default' : 'warning';

export default function CartDetailPage() {
  const { cartId } = useParams<{ cartId?: string }>();
  const navigate = useNavigate();

  const { viewCart, viewLoading, viewError, loadCartDetails, clearView } = useCartStore();

  useEffect(() => {
    if (cartId) void loadCartDetails(Number(cartId));
    return () => clearView();
  }, [cartId, loadCartDetails, clearView]);

  const totals = useMemo(() => {
    const subtotal = viewCart?.items?.reduce((s, i) => s + (i.quantity * (i.price ?? 0)), 0) ?? 0;
    const lines = viewCart?.items?.length ?? 0;
    const items = viewCart?.items?.reduce((s, i) => s + i.quantity, 0) ?? 0;
    return { subtotal, lines, items };
  }, [viewCart]);

  if (viewLoading) {
    return <Box sx={{ p: 3 }}><Typography variant="h6">Loading cart…</Typography></Box>;
  }
  if (viewError) {
    return <Box sx={{ p: 3 }}><Alert severity="error">{viewError}</Alert></Box>;
  }
  if (!viewCart) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Cart not found</Typography>
        <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate('/carts')}>Back to list</Button>
      </Box>
    );
  }

  const status = (viewCart.status ?? '').toLowerCase();

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Cart Details
      </Typography>

      {/* Banner */}
      <Box
        sx={{
          mb: 2, p: 2, borderRadius: 2,
          background: 'linear-gradient(135deg, #1f8ef1, #20bda5)',
          color: '#fff',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Cart #{viewCart.cartId}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            size="small"
            label={(viewCart.status ?? '').toUpperCase() || 'OPEN'}
            color={statusToColor(viewCart.status) as any}
            variant="outlined"
            sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.35)', color: '#fff' }}
          />
          <Typography variant="body2" sx={{ opacity: 0.95 }}>
            Created on&nbsp;{formatDate(viewCart.createdOn)}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 140 }}>Part No.</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right" sx={{ width: 90 }}>UOM</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>Price</TableCell>
              <TableCell align="center" sx={{ width: 150 }}>Qty</TableCell>
              <TableCell align="right" sx={{ width: 140 }}>Line Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {viewCart.items.map(line => (
              <TableRow key={line.cartItemId}>
                <TableCell>{line.partNumber}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{line.description}</Typography>
                  {line.itemNo ? <Typography variant="caption" color="text.secondary">Item #{line.itemNo}</Typography> : null}
                </TableCell>
                <TableCell align="right">{line.uom ?? '-'}</TableCell>
                <TableCell align="right">{formatCurrency(line.price)}</TableCell>
                <TableCell align="center">{line.quantity}</TableCell>
                <TableCell align="right">{formatCurrency((line.price ?? 0) * line.quantity)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={6}><Divider /></TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={2} />
              <TableCell align="right" sx={{ fontWeight: 600 }}>Lines:</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>{totals.lines}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Items:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.items}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={4} />
              <TableCell align="right" sx={{ fontWeight: 700 }}>Subtotal:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(totals.subtotal)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, p: 2 }}>
          <Button variant="outlined" color="inherit" onClick={() => navigate(-1)}>
            Back
          </Button>
          <Button variant="contained" onClick={() => navigate('/carts')}>
            Back to list
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
